/**
 * Type definitions for Wealthica v2 integration
 */

import { WealthicaConnectionStatus } from './constants-v2';

/**
 * Wealthica connection options
 */
export interface WealthicaConnectionOptions {
  provider?: string;
  state?: string;
  redirectUri?: string;
  reference?: string;
}

/**
 * Wealthica connection data
 * This is the data stored for a connection
 */
export interface WealthicaConnection {
  /**
   * OAuth rotating institution ID from Wealthica API
   */
  institutionId: string;
  
  /**
   * Stable financial institution identifier (e.g., 'wealthsimple', 'demo')
   */
  institutionIdentifier: string;
  
  /**
   * Provider name (e.g., 'wealthica', 'demo')
   */
  providerName: string;
  
  /**
   * Timestamp when the connection was established
   */
  timestamp: number;
  
  /**
   * State parameter used for the connection (for verification)
   */
  state: string;

  /**
   * Bearer token for API access (if available)
   */
  bearerToken?: string;

  /**
   * Unique identifier for the connection in our database
   */
  connectionId?: string;

  /**
   * Login name used when connecting to Wealthica
   * This must be consistent across token exchanges and API calls
   */
  loginName?: string;
  
  /**
   * Original provider from callback (for demo detection)
   */
  originalProvider?: string;
  
  /**
   * ISO timestamp string of the last successful account sync
   */
  lastSyncAt?: string;
}

/**
 * Result from a Wealthica callback processing
 */
export interface WealthicaCallbackResult {
  /**
   * Whether the callback was processed successfully
   */
  success: boolean;
  
  /**
   * Bearer token for API access (if successful)
   */
  bearerToken?: string;
  
  /**
   * Connection data (if successful)
   */
  connectionData?: WealthicaConnection;
  
  /**
   * Original provider from callback (for demo detection)
   */
  originalProvider?: string;
  
  /**
   * Error message (if unsuccessful)
   */
  error?: string;
}

/**
 * Parameters extracted from a Wealthica callback URL
 */
export interface WealthicaCallbackParams {
  /**
   * Code or institution ID from the callback
   */
  code?: string;
  
  /**
   * Provider name from the callback
   */
  provider: string;
  
  /**
   * State parameter from the callback (for CSRF protection)
   */
  state?: string;
  
  /**
   * Error from the callback (if any)
   */
  error?: string;
  
  /**
   * Error description from the callback (if any)
   */
  errorDescription?: string;
}

/**
 * Wealthica account
 */
/**
 * Represents a Wealthica account with associated institution data
 * 
 * This interface has been updated to match the actual usage throughout the codebase
 * and to ensure type safety for all properties used in account-parsers.ts and other components
 */
export interface WealthicaAccount {
  id: string;
  name: string;
  type: string;
  balance: number;
  currency: string;
  institutionId: string;
  
  // Enhanced institution metadata
  institutionName?: string;
  institutionDisplayName?: string;
  institutionLogoUrl?: string | null;
  
  // Institution object retained for backward compatibility
  institution: {
    id: string;
    name: string;
  };
}

/**
 * Canonical Wealthica position shape for the app
 * Represents a financial position from the Wealthica API
 * Structure is based on the response from the Wealthica API v1.0
 */
export interface WealthicaPosition {
  id: string;
  accountId: string;
  accountName?: string;
  accountNumber?: string;
  symbol: string;
  name: string;
  quantity: number;
  book_value: number;
  market_value: number;
  currency: string;
  securityId?: string;
  securityType?: string;
}

/**
 * API error
 */
export class WealthicaApiError extends Error {
  /**
   * Error code
   */
  readonly code: string;
  
  /**
   * Additional error details
   */
  /**
   * Additional error details (type unknown; must be narrowed at use)
   */
  readonly details?: unknown;
  
  constructor(code: string, message: string, details?: any) {
    super(message);
    this.name = 'WealthicaApiError';
    this.code = code;
    this.details = details;
    
    // Ensures proper prototype chain for instanceof checks
    Object.setPrototypeOf(this, WealthicaApiError.prototype);
  }
}

/**
 * Connection result from Wealthica
 */
export interface WealthicaConnectionResult {
  /**
   * Whether the connection was successful
   */
  success: boolean;
  
  /**
   * Redirect URL to Wealthica Connect (if successful)
   */
  redirectUrl?: string;
  
  /**
   * State parameter used for CSRF protection (if successful)
   */
  state?: string;
  
  /**
   * Error message (if unsuccessful)
   */
  error?: string;
}
